import {
  cn,
  Icon,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@risksmart-app/atomic-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './style.module.scss';

type CollapsibleFilterPreviewProps = {
  /** The read-only filter pills; each widget supplies its own panel. */
  children: ReactNode;
  /** Number of configured filter tokens — used for the collapsed control label. */
  tokenCount: number;
  /**
   * When compact, the pills collapse into a floating filter icon so the chart
   * reclaims the space (RSP-4068 / RSP-4069); otherwise they render inline.
   */
  isCompact: boolean;
};

export const CollapsibleFilterPreview = ({
  children,
  tokenCount,
  isCompact,
}: CollapsibleFilterPreviewProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'dashboard' });

  const pills = <div className={styles.filterPreview}>{children}</div>;

  if (!isCompact) {
    return pills;
  }

  // Float the control out of flow so the pill row no longer consumes the
  // widget's vertical space; inset from the corner to clear the resize handle.
  return (
    <div className={cn('relative w-full')}>
      <div className={cn('absolute bottom-1 right-1 z-10')}>
        <Popover>
          <PopoverTrigger
            render={(triggerProps) => (
              <IconButton
                {...triggerProps}
                aria-label={t('show_filters', { count: tokenCount })}
                className={cn('border border-muted bg-neutral shadow-sm')}
              >
                <Icon name={'filter-funnel-01'} size={'sm'} />
              </IconButton>
            )}
          />
          <PopoverContent className={cn('max-w-md p-2')}>
            {pills}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
